import { Contract, ExtendedContract, GetAllContractsExtendedResponse, CreateContractRequest, DeleteContractFileRequest, AddContractFileRequest, RequestContractSignatureRequest, MarkContractSignatureRequest } from '@/types/payment';
import { FileObject } from '@/types/shared/file';
import { Capacitor } from '@capacitor/core';
import { api } from '@/integrations/clients/carAuction/carAuctionClient';
import { ContractSourceLike, normalizeContract } from '@/selectors/contractSelectors';

interface ListingWithVehicleCardId {
  vehicleCardId?: string;
}

export class ContractService {
  private static baseUrl = '/admin/contracts';
  private static apiBaseUrl =
    import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000/api/v1';

  static async getAllContracts(): Promise<Contract[]> {
    const response = await api.get<{ success: boolean; contracts: ContractSourceLike[] }>(this.baseUrl);
    return (response.contracts || []).map((contract) => normalizeContract(contract));
  }

  static async getContract(id: string): Promise<Contract> {
    const response = await api.get<{ success: boolean; data: ContractSourceLike }>(`${this.baseUrl}/${id}`);
    return normalizeContract(response.data);
  }

  static async checkExistingContractByListingId(listingId: string): Promise<Contract | null> {
    // This is handled by backend - createContract will return 409 if contract already exists
    // Just return null to indicate no check is needed
    return null;
  }

  // TODO: G├Âr om h├ñr, ha inte checkExistingContractByListingId.. ha bara CreateContract, 
  // Backend f├Ñr kolla om annons har kontrakt redan > och om JA > svara med 409. D├Ñ fr├Ñgar vi anv om de vill ers├ñtta > om JA > g├Âr nytt create anrop med replaceExisting = true!
  static async createContract(request: CreateContractRequest, replaceExisting: boolean = false): Promise<Contract> {
    const { MediaService } = await import('../media/mediaService');
    
    // If replacing, delete existing contract first
    if (replaceExisting) {
      try {
        // Get listing to find existing contract
        const listingResponse = await api.get<{ success: boolean; data: ListingWithVehicleCardId }>(`/listings/${request.listingId}`);
        const listing = listingResponse.data;
        
        if (listing?.vehicleCardId) {
          // Try to find and delete existing contract
          const contractsResponse = await api.get<{ success: boolean; contracts: ContractSourceLike[] }>(`/admin/contracts?search=${listing.vehicleCardId}`);
          const existingContract = contractsResponse.contracts?.find(
            (con) => con.vehicleCardId === listing.vehicleCardId,
          );
          
          if (existingContract) {
            await api.delete(`/admin/contracts/${existingContract.id}`);
          }
        }
      } catch (error) {
        // If deletion fails, continue with creation (might not exist)
        console.warn('Failed to delete existing contract:', error);
      }
    }
    
    // Create contract WITHOUT file first (lazy upload)
    const createResponse = await api.post<{ success: boolean; data: ContractSourceLike }>(this.baseUrl, {
      contractNumber: request.contractNumber,
      listingId: request.listingId,
      replaceExisting,
    });
    
    const createdContract = createResponse.data;
    
    // If file was provided, upload it now (lazy upload)
    if (request.contractFile && createdContract.id) {
      try {
        // Request presigned URL
        const { uploadUrl, s3Key } = await MediaService.requestPresignedUploadUrl(
          'contract',
          createdContract.id,
          {
            originalName: request.contractFile.name,
            mimeType: request.contractFile.type,
            sizeBytes: request.contractFile.size,
          }
        );
        
        // Upload file to S3
        await MediaService.uploadFileToS3(uploadUrl, request.contractFile);
        
        // Create Media record with contractId
        const mediaResponse = await MediaService.createMediaAfterUpload(
          s3Key,
          {
            originalName: request.contractFile.name,
            mimeType: request.contractFile.type,
            sizeBytes: request.contractFile.size,
          },
          { contractId: createdContract.id }
        );
        
        // Update status to UPLOADED
        await MediaService.updateMediaStatus(mediaResponse.id, 'UPLOADED');
        
        // Fetch updated contract to get presigned download URL
        const updatedResponse = await api.get<{ success: boolean; data: ContractSourceLike }>(`${this.baseUrl}/${createdContract.id}`);
        const updatedContract = updatedResponse.data;
        
        // Map to Contract format
        return normalizeContract(updatedContract);
      } catch (error) {
        console.error('Failed to upload contract file:', error);
        // Return contract without file if upload fails
        return normalizeContract(createdContract);
      }
    }
    
    // Map to Contract format (no file)
    return normalizeContract(createdContract);
  }

  static async updateContract(updateData: Partial<Contract> & { id: string }): Promise<Contract> {
    const { id, ...data } = updateData;
    
    const response = await api.put<{ success: boolean; data: ContractSourceLike }>(`/admin/contracts/${id}`, {
      contractNumber: data.contract_number,
    });
    
    const updatedContract = response.data;
    
    // Map to Contract format
    return normalizeContract(updatedContract);
  }

  static async deleteContract(id: string): Promise<void> {
    await api.delete(`/admin/contracts/${id}`);
  }

  static async sendContract(id: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>(`${this.baseUrl}/${id}/send`);
    return response;
  }

  static async requestSignature(request: RequestContractSignatureRequest): Promise<Contract> {
    const response = await api.post<{ success: boolean; data: ContractSourceLike }>(`${this.baseUrl}/${request.contractId}/request-signature`, {
      note: request.note,
    });
    return normalizeContract(response.data);
  }

  static async markSignature(request: MarkContractSignatureRequest): Promise<Contract> {
    const response = await api.post<{ success: boolean; data: ContractSourceLike }>(`${this.baseUrl}/${request.contractId}/mark-signature`, {
      method: request.method.toUpperCase(),
      note: request.note,
    });
    return normalizeContract(response.data);
  }

  static getBankIdSignatureStartUrl(contractId: string): string {
    return `${this.apiBaseUrl.replace(/\/$/, '')}/listings/contracts/${contractId}/sign-bankid`;
  }

  static async getNativeBankIdSignatureContinueUrl(contractId: string): Promise<string> {
    const data = await api.post<{ success: boolean; continueUrl: string }>(
      `/listings/contracts/${contractId}/sign-bankid/native-start`,
      { nativePlatform: Capacitor.getPlatform() },
    );
    return data.continueUrl;
  }

  /** Finalize BankID signature when Idura webhook is delayed/missing. */
  static async finalizePendingBankIdSignature(params?: {
    signatoryId?: string | null;
    signatureOrderId?: string | null;
  }): Promise<{ signed: boolean; contractId?: string | null; reason?: string | null }> {
    const data = await api.post<{
      success: boolean;
      signed: boolean;
      contractId?: string | null;
      reason?: string | null;
    }>('/listings/contracts/signatures/finalize-pending', {
      signatoryId: params?.signatoryId || undefined,
      signatureOrderId: params?.signatureOrderId || undefined,
    });
    return {
      signed: Boolean(data.signed),
      contractId: data.contractId ?? null,
      reason: data.reason ?? null,
    };
  }

  //TODO: Flytta till paymentService? getPaymentContracts
  static async getContractsByPaymentId(paymentId: string): Promise<Contract[]> {
    // TODO: Backend endpoint for getting contracts by paymentId not yet implemented
    // For now, return empty array
    return [];
  }


  static async getAllContractsExtended(page: number = 1, limit: number = 10, status?: string, search?: string): Promise<GetAllContractsExtendedResponse> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (status && status !== 'all') params.append('status', status);
    if (search) params.append('search', search);
    
    const response = await api.get<{ success: boolean; contracts: ExtendedContract[]; total: number; page: number; limit: number }>(`/admin/contracts?${params.toString()}`);
    
    // Backend now returns ExtendedContract format directly, no mapping needed
    return {
      contracts: response.contracts,
      total: response.total,
      page: response.page,
      limit: response.limit,
    };
  }

  // Delete contract file (deletes Media object)
  static async deleteContractFile(request: DeleteContractFileRequest): Promise<{ status: string; message: string }> {
    try {
      // Use updateMedia endpoint to set mediaId to null
      const response = await api.put<{ success: boolean; data: unknown }>(`${this.baseUrl}/${request.contractId}/media`, { mediaId: null });
      if (response.success) {
        return { status: 'success', message: 'Contract file deleted successfully' };
      }
      throw new Error('Failed to delete contract file');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete contract file';
      throw new Error(errorMessage);
    }
  }

  // Add contract file (lazy upload pattern)
  static async addContractFile(request: AddContractFileRequest): Promise<FileObject> {
    try {
      const { MediaService } = await import('../media/mediaService');
      
      // Request presigned URL
      const { uploadUrl, s3Key } = await MediaService.requestPresignedUploadUrl(
        'contract',
        request.contractId,
        {
          originalName: request.contractFile.name,
          mimeType: request.contractFile.type,
          sizeBytes: request.contractFile.size,
        }
      );
      
      // Upload file to S3
      await MediaService.uploadFileToS3(uploadUrl, request.contractFile);
      
      // Create Media record with contractId
      const mediaResponse = await MediaService.createMediaAfterUpload(
        s3Key,
        {
          originalName: request.contractFile.name,
          mimeType: request.contractFile.type,
          sizeBytes: request.contractFile.size,
        },
        { contractId: request.contractId }
      );
      
      // Update status to UPLOADED
      await MediaService.updateMediaStatus(mediaResponse.id, 'UPLOADED');
      
      return {
        id: mediaResponse.id,
        url: '', // Will be fetched on-demand via presigned URL
        name: mediaResponse.originalName,
        type: mediaResponse.mimeType,
        size: mediaResponse.sizeBytes,
        mediaId: mediaResponse.id,
      };
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to add contract file';
      throw new Error(errorMessage);
    }
  }
}

